import {
  HStack,
  Pressable,
  Text,
  VStack,
} from '@ayble-health/dashboard-components';
import React from 'react';
import { Center, useToken } from '@chakra-ui/react';
import { BarChart4, ThumbsDown, ThumbsUp } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { useUser } from '../../../api/healthCoach/useUser';
import { useClaimRiskStrat } from '../../../api/healthCoach/useClaimRiskStrat';
import { useUserFeedbackSurvey } from '../../../api/healthCoach/useUserFeedback';
import { useUpdateClaimRiskStrat } from '../../../api/healthCoach/mutations/useUpdateClaimRiskStrat';
import { useCreateUserFeedback } from '../../../api/healthCoach/mutations/useCreateUserFeedback';
import { Loader } from '../../Loader';
import { RiskSquares } from '../../ui/Severity/RiskSquares';
import type { RiskLevel } from '../../../models/user';
import { FEEDBACK_TYPE, SENTIMENT } from '../../../models/userFeedback';
import { stripHtml } from './utils';

type InsightFeedbackCardProps = {
  // Omit for insights that have no risk value; RiskSquares is hidden then.
  level?: RiskLevel | null;
  description: string;
  helpful: boolean | null;
  onFeedback: (value: boolean | null) => void;
  disabled?: boolean;
};

const InsightFeedbackCard = ({
  level,
  description,
  helpful,
  onFeedback,
  disabled,
}: InsightFeedbackCardProps) => {
  const [steel, canvas] = useToken('colors', ['steel', 'canvas']);

  const handleThumbClick = (thumb: 'up' | 'down') => {
    if (thumb === 'up') {
      onFeedback(helpful === true ? null : true);
    } else {
      onFeedback(helpful === false ? null : false);
    }
  };

  return (
    <VStack
      alignItems={'flex-start'}
      bg={'canvas'}
      borderColor={'cloud.200'}
      borderWidth={1}
      gap={2}
      p={3}
      rounded={'md'}
      w={'100%'}
    >
      {level !== undefined && <RiskSquares level={level} />}
      <HStack
        alignItems={'flex-start'}
        gap={2}
        w={'100%'}
        justifyContent={'space-between'}
      >
        <Text
          color={helpful === false ? 'steel' : 'secondary.500'}
          fontSize={'sm'}
        >
          {description}
        </Text>
        <HStack gap={2} flexShrink={0} alignItems={'center'}>
          <Pressable
            aria-label={'Mark this insight as helpful'}
            aria-pressed={helpful === true}
            disabled={disabled}
            onClick={() => handleThumbClick('up')}
          >
            {helpful === true ? (
              <Center bg={'grass'} borderRadius={'full'} boxSize={'20px'}>
                <ThumbsUp color={canvas} size={12} />
              </Center>
            ) : (
              <ThumbsUp color={steel} size={16} />
            )}
          </Pressable>
          <Pressable
            aria-label={'Mark this insight as not helpful'}
            aria-pressed={helpful === false}
            disabled={disabled}
            onClick={() => handleThumbClick('down')}
          >
            {helpful === false ? (
              <Center bg={'tomato'} borderRadius={'full'} boxSize={'20px'}>
                <ThumbsDown color={canvas} size={12} />
              </Center>
            ) : (
              <ThumbsDown color={steel} size={16} />
            )}
          </Pressable>
        </HStack>
      </HStack>
    </VStack>
  );
};

type Props = {
  onFeedbackSaved: () => void;
};

export const PatientSummaryTab = ({ onFeedbackSaved }: Props) => {
  const { patient: userId } = useParams();
  const [seafoam] = useToken('colors', ['seafoam']);
  const { user, isLoading: userLoading } = useUser();
  const patientSummary = user?.patient_summary
    ? stripHtml(user.patient_summary) || null
    : null;
  const { claimRiskStrat, isLoading: claimRiskStratLoading } =
    useClaimRiskStrat({ enabled: !userLoading && !patientSummary });
  const { mutation: updateClaimRiskStrat } = useUpdateClaimRiskStrat(
    userId ?? ''
  );
  const { mutation: createUserFeedback } = useCreateUserFeedback();
  const { userFeedbackSurveyData: patientSummaryFeedback } =
    useUserFeedbackSurvey({
      survey_type: FEEDBACK_TYPE.CHIEF_COMPLAINT_FEEDBACK,
      order_by: 'desc',
      enabled: !!patientSummary,
    });
  const savedSentiment = patientSummaryFeedback?.[0]?.survey?.sentiment;
  const patientSummaryHelpful =
    savedSentiment === SENTIMENT.positive
      ? true
      : savedSentiment === SENTIMENT.negative
      ? false
      : null;

  const clinicalVolatilityDesc = claimRiskStrat?.clinical_volatility_desc || null;
  const medicationManagementDesc = claimRiskStrat?.medication_management_desc || null;
  const hasClaimInsights = !!clinicalVolatilityDesc || !!medicationManagementDesc;

  const handleFeedback = async (
    field: 'medication_management_helpful' | 'clinical_volatility_helpful',
    value: boolean | null
  ) => {
    try {
      await updateClaimRiskStrat.mutateAsync({ [field]: value });
      onFeedbackSaved();
    } catch {
      // useApi already logs the failure; no dedicated error UI for this yet.
    }
  };

  const handlePatientSummaryFeedback = async (value: boolean | null) => {
    // Un-toggling a thumb is recorded as neutral, keeping a log of every change.
    const sentiment =
      value === null
        ? SENTIMENT.neutral
        : value
        ? SENTIMENT.positive
        : SENTIMENT.negative;

    try {
      await createUserFeedback.mutateAsync({
        user_block_id: null,
        survey: { sentiment },
        feedback_day: new Date().toISOString().split('T')[0],
        survey_type: FEEDBACK_TYPE.CHIEF_COMPLAINT_FEEDBACK,
      });
      onFeedbackSaved();
    } catch {
      // useApi already logs the failure; no dedicated error UI for this yet.
    }
  };

  if (userLoading) {
    return <Loader loading />;
  }

  if (patientSummary) {
    return (
      <InsightFeedbackCard
        description={patientSummary}
        disabled={createUserFeedback.isLoading}
        helpful={patientSummaryHelpful}
        onFeedback={handlePatientSummaryFeedback}
      />
    );
  }

  if (claimRiskStratLoading) {
    return <Loader loading />;
  }

  if (!hasClaimInsights) {
    return (
      <Text color={'blueGray.600'} fontSize={'sm'} fontStyle={'italic'}>
        No patient summary available for this member.
      </Text>
    );
  }

  return (
    <VStack alignItems={'flex-start'} gap={3} pt={1} w={'100%'}>
      <HStack alignItems={'center'} gap={2}>
        <Center bg={'shortbread'} borderRadius={'full'} boxSize={'24px'}>
          <BarChart4 color={seafoam} size={14} />
        </Center>
        <Text color={'secondary.500'} fontSize={'sm'} fontWeight={'semibold'}>
          Pre-initial visit claims data insights
        </Text>
      </HStack>

      <VStack gap={2} w={'100%'}>
        {clinicalVolatilityDesc && (
          <InsightFeedbackCard
            description={clinicalVolatilityDesc}
            disabled={updateClaimRiskStrat.isLoading}
            helpful={claimRiskStrat?.clinical_volatility_helpful ?? null}
            level={claimRiskStrat?.clinical_volatility_value || null}
            onFeedback={(value) =>
              handleFeedback('clinical_volatility_helpful', value)
            }
          />
        )}
        {medicationManagementDesc && (
          <InsightFeedbackCard
            description={medicationManagementDesc}
            disabled={updateClaimRiskStrat.isLoading}
            helpful={claimRiskStrat?.medication_management_helpful ?? null}
            level={claimRiskStrat?.medication_management_value || null}
            onFeedback={(value) =>
              handleFeedback('medication_management_helpful', value)
            }
          />
        )}
      </VStack>
    </VStack>
  );
};

export default PatientSummaryTab;
